import "@/App.css";
import '@/i18n';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { Toaster } from "sonner";

// Import pages (we'll create these next)
import LandingPage from "@/pages/LandingPage";
import GuardianLogin from "@/pages/GuardianLogin";
import GuardianRegister from "@/pages/GuardianRegister";
import ForgotPassword from "@/pages/ForgotPassword";
import GuardianPortal from "@/pages/GuardianPortal";
import StudentAcademy from "@/pages/StudentAcademy";
import TeacherPortal from "@/pages/TeacherPortal";
import AdminPortal from "@/pages/AdminPortal";
import StudentLogin from "@/pages/StudentLogin";
import DonatePage from "@/pages/DonatePage";
import BrandPortal from "@/pages/BrandPortal";
import ArtistPortal from "@/pages/ArtistPortal";
import AffiliateSignup from "@/pages/AffiliateSignup";
import AudioBooksPage from "@/pages/AudioBooksPage";
import SharedRecordingPage from "@/pages/SharedRecordingPage";
import ShareScreenPage from "@/pages/ShareScreenPage";
import StudentMyMusic from "@/pages/StudentMyMusic";
import { MusicPlayerProvider } from "@/contexts/MusicPlayerContext";
import MusicPlayerBar from "@/components/student/MusicPlayerBar";
import { ChatButton } from "@/components/student/ChatPanel";
import SupportWidget from "@/components/SupportWidget";

// Protected route component
const ProtectedRoute = ({ children, requireAuth = true, allowedRoles = null }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl font-bold">Loading...</div>
      </div>
    );
  }

  // Check both context state and localStorage (handles race condition after login)
  const hasToken = !!localStorage.getItem('token') && !!localStorage.getItem('user');
  if (requireAuth && !isAuthenticated && !hasToken) {
    return <Navigate to="/login" replace />;
  }

  // Role-based access control
  if (allowedRoles) {
    const savedUser = user || JSON.parse(localStorage.getItem('user') || '{}');
    const userRole = savedUser?.role;
    const isDelegatedAdmin = savedUser?.is_delegated_admin;
    // Allow delegated admins to access admin routes
    if (!allowedRoles.includes(userRole) && !(allowedRoles.includes('admin') && isDelegatedAdmin)) {
      // Redirect to the correct portal for their role
      const roleRedirects = { brand_partner: '/brand-portal', artist: '/artist-portal', admin: '/admin', teacher: '/portal', guardian: '/portal' };
      return <Navigate to={roleRedirects[userRole] || '/login'} replace />;
    }
  }

  return children;
};

// Student protected route with persistent music player
const StudentRoute = ({ children }) => {
  const { student } = useAuth();

  if (!student) {
    return <Navigate to="/student-login" replace />;
  }

  return (
    <MusicPlayerProvider studentId={student?.id}>
      {children}
      <MusicPlayerBar />
      <ChatButton />
    </MusicPlayerProvider>
  );
};

function AppContent() {
  const { isAuthenticated } = useAuth();
  return (
    <BrowserRouter>
      <div className="App min-h-screen bg-gray-50">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/:lang" element={<LandingPage />} />
          <Route path="/login" element={<GuardianLogin />} />
          <Route path="/register" element={<GuardianRegister />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/student-login" element={<StudentLogin />} />
          <Route path="/teacher-login" element={<Navigate to="/login?type=teacher" replace />} />
          <Route path="/teacher-register" element={<Navigate to="/register?role=teacher" replace />} />
          <Route path="/donate" element={<DonatePage />} />
          <Route path="/affiliate" element={<AffiliateSignup />} />
          <Route path="/audio-books" element={<AudioBooksPage />} />
          <Route path="/shared/:token" element={<SharedRecordingPage />} />
          <Route path="/share/:code" element={<ShareScreenPage />} />

          {/* Guardian/Teacher protected routes */}
          <Route
            path="/portal/*"
            element={
              <ProtectedRoute allowedRoles={['guardian', 'teacher', 'admin']}>
                <GuardianPortal />
              </ProtectedRoute>
            }
          />

          {/* Teacher portal redirects to guardian portal (merged) */}
          <Route
            path="/teacher-portal/*"
            element={<Navigate to="/portal" replace />}
          />

          {/* Admin protected routes - ADMIN ONLY */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPortal />
              </ProtectedRoute>
            }
          />

          {/* Brand Portal protected routes */}
          <Route
            path="/brand-portal"
            element={
              <ProtectedRoute allowedRoles={['brand_partner', 'admin']}>
                <BrandPortal />
              </ProtectedRoute>
            }
          />

          {/* Artist Portal protected routes */}
          <Route
            path="/artist-portal"
            element={
              <ProtectedRoute allowedRoles={['artist', 'admin']}>
                <ArtistPortal />
              </ProtectedRoute>
            }
          />

          {/* Student protected routes */}
          <Route
            path="/academy/my-music"
            element={
              <StudentRoute>
                <StudentMyMusic />
              </StudentRoute>
            }
          />
          <Route
            path="/academy/*"
            element={
              <StudentRoute>
                <StudentAcademy />
              </StudentRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
        {(isAuthenticated || localStorage.getItem('student')) && <SupportWidget />}
      </div>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <AppContent />
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
// cache bust 1775004685
